import { useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { apiCallingPost } from '../api/services';
import dayjs from 'dayjs';
import {
    setLoadingTag,
    setTagNames,
    setTotalCountTag,
    setTagList,
    setTagListIsFallback,
    setOffsetTag,
    setShowMoreTag,
} from '../redux/reducers/commonSlice';

/**
 * Encapsulates all tag search logic: debouncing, API call, Redux dispatch.
 *
 * Watches search state from Redux and fires a debounced search whenever any
 * filter changes. Returns { searchTag } for manual invocation if needed.
 *
 * @param {object} opts
 * @param {function} opts.setVideoListFlag - Callback to show/hide the results panel
 */
function useSearch({ setVideoListFlag }) {
    const dispatch = useDispatch();
    const debounceTimer = useRef(null);
    const requestIdRef = useRef(0);
    const isMounted = useRef(false);

    const {
        fileType, searchText, locationName, eventName, searchType,
        selectMonthAndYear, selectYear, offsetTag, pageSizeTag,
        orientation, showMoreTag,
    } = useSelector((state) => state.commonData);

    const prevTexts = useRef({ searchText, locationName, eventName });

    // Keep showMoreTag in a ref so the debounced searchTag always reads the
    // latest value — not the stale closure from when the timer was set.
    const showMoreTagRef = useRef(showMoreTag);
    useEffect(() => { showMoreTagRef.current = showMoreTag; });

    const searchTag = async () => {
        const thisRequestId = ++requestIdRef.current;
        dispatch(setLoadingTag(true));
        try {
            if (!showMoreTagRef.current) {
                setVideoListFlag?.(true);
            }

            const month = selectMonthAndYear ? dayjs(selectMonthAndYear).format('MMM YYYY') : '';
            const year = selectYear ? dayjs(selectYear).format('YYYY') : '';

            const postData = {
                searchText,
                fileType,
                offSet: offsetTag,
                pagesize: pageSizeTag,
                videoLocation: locationName,
                videoDate: month + year,
                searchType: searchType === 1 ? '' : 'video',
                eventName,
                orientation,
            };

            const response = await apiCallingPost('api/common/tagSearch', postData);

            if (thisRequestId !== requestIdRef.current) return; // stale response — a newer request is in flight

            if (response.data.success === '1') {
                let result = response.data.result;
                let totalCount = 0;

                dispatch(setTagNames(result.nameSuggestion || []));

                let isFallback = false;
                if (result.matched && result.matched.length > 0) {
                    result = result.matched;
                    totalCount = response.data.totalCount;
                } else {
                    // Nothing matched every word: the server sends files matching
                    // SOME of the words. The UI labels these, never as "results for".
                    result = result.suggestions || [];
                    totalCount = response.data.suggestionCount;
                    // Only a FIRST page can be a fallback; a later page that comes
                    // back empty (rows retired mid-scroll) must not relabel the list.
                    isFallback = offsetTag === 0 && result.length > 0;
                }

                dispatch(setTotalCountTag(totalCount));
                dispatch(setTagList(result));
                dispatch(setTagListIsFallback(isFallback));
            } else {
                dispatch(setTagList([]));
                dispatch(setTagNames([]));
                dispatch(setTagListIsFallback(false));
            }
        } catch (error) {
            if (thisRequestId !== requestIdRef.current) return; // stale error — discard
            console.error('Search error:', error);
            dispatch(setTagList([]));
            dispatch(setTagNames([]));
            dispatch(setTagListIsFallback(false));
        } finally {
            // Only clear loading for the latest request — earlier ones are discarded
            if (thisRequestId === requestIdRef.current) {
                dispatch(setLoadingTag(false));
            }
        }
    };

    useEffect(() => {
        if (
            searchText.length >= 2 ||
            locationName.length >= 2 ||
            eventName.length >= 2 ||
            selectMonthAndYear ||
            selectYear ||
            orientation !== ''
        ) {
            // (searchType — the "Search by segment" box — is a MODE, not a query: it is
            // in the deps below so toggling it re-runs a search that has a term or a
            // filter, but on its own it must not list the whole library.)
            const textChanged =
                prevTexts.current.searchText !== searchText ||
                prevTexts.current.locationName !== locationName ||
                prevTexts.current.eventName !== eventName;

            prevTexts.current = { searchText, locationName, eventName };
            clearTimeout(debounceTimer.current);

            if (!isMounted.current || textChanged) {
                // First mount or text field changed — debounce to avoid per-keystroke calls
                debounceTimer.current = setTimeout(() => { searchTag(); }, 600);
            } else {
                // Discrete filter change (tab, orientation, date, pagination) — fire immediately
                searchTag();
            }

            isMounted.current = true;
        } else {
            prevTexts.current = { searchText, locationName, eventName };
            isMounted.current = false;
            setVideoListFlag?.(false);
            dispatch(setOffsetTag(0));
            dispatch(setShowMoreTag(false));
        }
        return () => clearTimeout(debounceTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchText, fileType, locationName, eventName, selectMonthAndYear, selectYear, searchType, orientation, offsetTag, pageSizeTag]);

    return { searchTag };
}

export default useSearch;
